import * as THREE from 'three';
import { addFakeLights } from '../fx/fakeLights';
import type { Materials } from '../scene/materials';
import { isBoss, type Enemy as SimEnemy, type EnemyClass, type BossClass, type Sim, type SimEvent } from '@orb/sim';
import { L, type Kind } from './config';
import { emergenceAt, routeAt } from './EnemyRoute';
import { fxUniforms } from '../fx/common';
import { WARRIOR_POSE } from '../fx/warriorPose';
import { buildWarrior, type WarriorTemplate } from './warrior';

export { isBoss, type EnemyClass, type BossClass };

/**
 * One enemy as drawn: the simulation's record (`e`, read-only here) plus everything that only
 * matters on screen. After a death or breach the sim drops the record; the view keeps its last
 * state and plays the dissolve or the triumph on its own.
 */
export interface Enemy {
  id: number;
  e: SimEnemy;
  cls: EnemyClass;
  tpl: number; slot: number;
  /** 0 while alive, then 0→1 dissolve. */
  dying: number;
  /** After breaching the gate: 0…1 of a short triumph (weapon raised, then fading out). */
  triumph: number;
  leaked: boolean;
  phase: number; gait: number;
  renderedS: number; renderedSide: number;
  hit: number; hitKind: Kind;
  /** Glance at a fallen neighbour: turn side (+ = the body's right) and the seconds left of it. */
  glanceDir: number; glanceT: number;
  reveal: number;
  ry: number;
  /** Interpolated render position (feet). */
  pos: THREE.Vector3;
}

interface Tpl { t: WarriorTemplate; meshes: THREE.InstancedMesh[]; matrix: THREE.InstancedBufferAttribute; anim: THREE.InstancedBufferAttribute; fx: THREE.InstancedBufferAttribute; crowd: THREE.InstancedBufferAttribute; persona: THREE.InstancedBufferAttribute; glow: THREE.InstancedBufferAttribute; slots: (Enemy | null)[]; cap: number; used: number }

/** Injects walk cycle, hit flash, status tints and a burning dissolve into a stock material. */
function patch(base: THREE.Material): THREE.Material {
  const m = base.clone();
  const basic = (m as THREE.MeshBasicMaterial).isMeshBasicMaterial;
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uTime = fxUniforms.uTime;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>
        attribute float aPart; attribute vec4 iAnim; attribute vec4 iFx; attribute vec4 iCrowd; attribute vec4 iPersona; attribute vec4 iGlow;
        attribute vec4 aSurf, aSurf2; varying vec4 vSurf; varying vec3 vSurf2;
        varying vec3 vLocal; varying vec4 vAnim; varying vec4 vFx; varying float vElement, vHitRim, vPersona; varying vec3 vGlow, vFogW;
        ${WARRIOR_POSE}`)
      .replace('void main() {', `void main() {
        vec3 posedPosition = position, posedNormal = normal;
        warriorPose(posedPosition, posedNormal);`)
      .replace('#include <beginnormal_vertex>', '#include <beginnormal_vertex>\nobjectNormal = posedNormal;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        {
          transformed = posedPosition;
          vLocal = position; vAnim = iAnim; vFx = iFx; vElement = iCrowd.w; vPersona = iPersona.x; vGlow = iGlow.xyz;
          vSurf = aSurf; vSurf2 = aSurf2.xyz;
        }`)
      .replace('#include <project_vertex>', `#include <project_vertex>
        vFogW = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;
        vec3 hitNormal = normalize(mat3(modelViewMatrix * instanceMatrix) * posedNormal);
        vHitRim = pow(1.0 - abs(dot(hitNormal, normalize(-mvPosition.xyz))), 2.0);`);
    const head = `varying vec4 vSurf; varying vec3 vSurf2;
      varying vec3 vLocal; varying vec4 vAnim; varying vec4 vFx; varying float vElement, vHitRim, vPersona; varying vec3 vGlow, vFogW;
      uniform float uTime;
      float dHash(vec3 p) { return fract(sin(dot(p, vec3(12.9898, 78.233, 37.719))) * 43758.5453); }
      // Smooth value noise in model space: stable under camera motion, unlike a screen-space dither.
      float dNoise(vec3 p) {
        vec3 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
        return mix(mix(mix(dHash(i), dHash(i + vec3(1,0,0)), f.x), mix(dHash(i + vec3(0,1,0)), dHash(i + vec3(1,1,0)), f.x), f.y),
                   mix(mix(dHash(i + vec3(0,0,1)), dHash(i + vec3(1,0,1)), f.x), mix(dHash(i + vec3(0,1,1)), dHash(i + vec3(1,1,1)), f.x), f.y), f.z);
      }`;
    const dissolve = `
      float mist = 0.0, edge = 0.0;
      if (vFx.y < 0.999) {
        // condense out of the fog: noise threshold with a cold rim at the forming edge
        float n = dNoise(vLocal * 5.0) * 0.85 + dNoise(vLocal * 17.0) * 0.15, k = vFx.y * 1.08;
        if (n > k) discard;
        mist = smoothstep(k - 0.1, k, n) * (1.0 - vFx.y * 0.6);
      }
      if (vAnim.z > 0.0) {
        // the body falls first, then burns away from the noise up
        float burn = max(0.0, vAnim.z - 0.22) / 0.78 * 1.15;
        float dn = dNoise(vLocal * 11.0) * 0.8 + dNoise(vLocal * 29.0) * 0.12 + vLocal.y * 0.08;
        if (dn < burn) discard;
        edge = burn > 0.0 ? smoothstep(burn + 0.12, burn, dn) : 0.0;
      }
      vec3 hitColor = vElement > 7.5 ? vec3(1.0, 0.35, 0.72) : vElement < 0.5 ? vec3(1.0, 0.24, 0.06) : vElement < 1.5 ? vec3(0.15, 0.55, 1.0) : vElement < 2.5 ? vec3(1.0, 0.72, 0.18) : vElement < 3.5 ? vec3(0.22, 0.7, 0.12) : vElement < 5.5 ? vec3(0.62, 0.35, 1.0) : vElement < 6.5 ? vec3(1.0, 0.08, 0.25) : vec3(1.0, 0.7, 0.3);
      float rim = 0.06 + vHitRim * 0.55;
      // frozen solid: a glassy blue glaze, rime settled only on upward faces in a fine grain,
      // and an icy glow along the silhouette
      float stasis = step(vFx.w, -3.5);
      float frozen = step(vFx.w, -0.5) * (1.0 - stasis);
      vec3 faceN = normalize(cross(dFdx(vFogW), dFdy(vFogW)));
      // rime only on the frozen: the 3D noise is 8 hashes, too dear to multiply by zero on every other enemy pixel
      float crust = 0.0;
      if (frozen > 0.5) crust = smoothstep(0.3, 0.75, faceN.y) * smoothstep(0.42, 0.62, dNoise(vLocal * 30.0 + 4.0));
      // Status and hit light hug the true silhouette edge (rim²) so stacked states tint an enemy
      // instead of washing it out; their sum is soft-saturated. Dissolve edges and fog rims stay bright.
      float edgeRim = vHitRim * vHitRim;
      // bosses take hits on their seams instead: under constant fire a body-wide tint would bleach their dark armour
      float hitBody = vAnim.y * (1.0 - 0.8 * vGlow.x);
      vec3 status = hitColor * hitBody * (0.05 + edgeRim * 0.9)
        + vec3(0.1, 0.45, 1.1) * vAnim.w * (0.02 + edgeRim * 0.45)
        + vec3(0.25, 0.8, 0.12) * vFx.x * (0.02 + edgeRim * 0.35)
        + vec3(1.0, 0.9, 0.82) * pow(hitBody, 5.0) * 0.18
        + (vec3(0.3, 0.6, 1.1) * edgeRim * 0.7 + vec3(0.55, 0.8, 1.1) * crust * 0.12) * frozen
        + vec3(1.0, 0.65, 0.23) * stasis * (edgeRim * 0.9 + pow(max(0.0, cos(vLocal.y * 24.0)), 20.0) * 0.12);
      status /= 1.0 + max(status.r, max(status.g, status.b)) * 0.8;
      // the same dissolve condenses enemies out of the cold fog and burns breachers away in the portal's fire
      float atGate = smoothstep(${(L.gateX - 2.6).toFixed(2)}, ${(L.gateX - 1.2).toFixed(2)}, vFogW.x);
      vec3 mistColor = mix(vec3(0.22, 0.34, 0.46) * 0.8, vec3(1.6, 0.55, 0.14) * 1.4, atGate);
      vec3 fxGlow = status + hitColor * edge * 2.4 + mistColor * mist;`;
    sh.fragmentShader = sh.fragmentShader.replace('#include <common>', `#include <common>\n${head}`)
      // inside the spawn fog the body is veiled by the same bank colour, thicker toward the ground,
      // so enemies read as walking in the fog rather than over it
      .replace('#include <dithering_fragment>', `#include <dithering_fragment>
        float veil = (1.0 - smoothstep(${(L.fogStartX - 0.8).toFixed(2)}, ${(L.fogEndX + 0.1).toFixed(2)}, vFogW.x)) * mix(1.0, 0.7, smoothstep(0.1, 1.6, vFogW.y));
        gl_FragColor.rgb = mix(gl_FragColor.rgb, vec3(0.062, 0.083, 0.115), veil * 0.85);`);
    if (basic) {
      sh.fragmentShader = sh.fragmentShader.replace('#include <color_fragment>', `#include <color_fragment>\n diffuseColor.rgb *= vSurf.rgb;\n${dissolve}\n diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.18, 1.0, 1.8), vFx.z); diffuseColor.rgb += fxGlow;`);
    } else {
      const cloth = base.side === THREE.DoubleSide;
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <color_fragment>', `#include <color_fragment>
          diffuseColor.rgb *= vSurf.rgb;
          ${cloth ? `if (vSurf2.z > 0.5) {
            // dyed wool, not paper: muted dyes, vertical folds by angle around the body,
            // a shadowed inner face and a hem darkened with road dirt
            vec3 clothColor = vPersona < 0.33 ? vec3(.15,.032,.024) : vPersona < 0.66 ? vec3(.022,.085,.09) : vec3(.15,.095,.036);
            float fold = 0.72 + 0.28 * sin(atan(vLocal.x, vLocal.z) * 13.0 + vPersona * 20.0 + sin(vLocal.y * 4.0) * 0.8);
            float hem = mix(0.55, 1.0, smoothstep(0.1, 0.6, vLocal.y));
            diffuseColor.rgb = clothColor * (0.85 + fract(vPersona * 17.0) * 0.3) * fold * hem * (gl_FrontFacing ? 1.0 : 0.55);
          } else ` : ''}diffuseColor.rgb *= mix(vec3(.76,.87,1.04),vec3(1.18,.94,.7),vPersona);
          if (vFx.w < -3.5) diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.28, 0.19, 0.08), 0.25);
          else if (vFx.w < -0.5) diffuseColor.rgb = mix(mix(diffuseColor.rgb, vec3(0.1, 0.22, 0.36), 0.6), vec3(0.8, 0.9, 1.0), crust * 0.75);`)
        .replace('#include <metalnessmap_fragment>', '#include <metalnessmap_fragment>\n roughnessFactor = vSurf.w; metalnessFactor = vSurf2.x;\n metalnessFactor = mix(metalnessFactor, 0.05, frozen); roughnessFactor = mix(roughnessFactor, 0.12, frozen * (1.0 - crust));')
        .replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>\n${dissolve}`)
        .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>\n totalEmissiveRadiance += fxGlow;
          // a faint cold moonlit rim, stronger on upper faces, lifts the silhouette off the dark road
          totalEmissiveRadiance += vec3(0.22, 0.32, 0.5) * edgeRim * (0.06 + 0.12 * max(faceN.y, 0.0)) * vFx.y;${!cloth ? `
          if (vGlow.x > 0.0 && vSurf2.z > 1.5) {
            // molten seams: two scales of noise ridges read as cracks between armour plates;
            // they pulse like a heartbeat that quickens and brightens as the boss is worn down
            vec3 seamColor = vGlow.y > 0.5 ? vec3(0.62, 0.3, 1.0) : vec3(1.0, 0.3, 0.07);
            float r1 = abs(dNoise(vLocal * 7.0 + 3.1) - 0.5), r2 = abs(dNoise(vLocal * 16.0 + 9.7) - 0.5);
            // a low-frequency mask clusters the cracks, leaving whole plates dark between them;
            // damage widens the mask so a worn-down boss is visibly split open
            float zone = smoothstep(0.62 - vGlow.z * 0.35, 0.82 - vGlow.z * 0.3, dNoise(vLocal * 2.3 + 1.3));
            float vein = (smoothstep(0.03, 0.0, r1) + smoothstep(0.022, 0.0, r2) * 0.3) * (0.15 + 0.85 * zone);
            float beat = 0.72 + 0.28 * sin(uTime * (2.0 + vGlow.z * 5.0) + vPersona * 6.283);
            float power = (1.1 + vGlow.z * 2.4) * beat + vAnim.y * 1.2;
            totalEmissiveRadiance += seamColor * (vein * power + vHitRim * (0.18 + vGlow.z * 0.3)) * vGlow.x * vFx.y;
          }` : ''}`);
    }
  };
  m.customProgramCacheKey = () => 'horde-persona-v23-' + base.name;
  if (!basic) addFakeLights(m);
  return m;
}


const _q2 = new THREE.Quaternion(), _x = new THREE.Vector3(1, 0, 0);
const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _s = new THREE.Vector3(), _p = new THREE.Vector3(), _up = new THREE.Vector3(0, 1, 0), _direction = new THREE.Vector3();

/**
 * The march as drawn. Every class is a set of instanced meshes (one per material) that
 * share instance-matrix and pose buffers. Adding enemies does not add draws;
 * an unused class has no draw calls, and uploads cover only active slots.
 */
export class Horde {
  private warnedFull = false;
  readonly enemies: Enemy[] = [];
  private readonly byId = new Map<number, Enemy>();
  /** Heavy or fast enemies whose foot landed this render frame (consumed by Combat). */
  readonly footfalls: Enemy[] = [];
  private readonly tpls: Tpl[] = [];
  private readonly blobs: THREE.InstancedMesh;
  private sim: Sim | null = null;

  /** `detail` (quality tier) scales the segment counts of every enemy model. */
  private readonly defs: [number, BossClass | 'runner' | null, number][];
  detail: number;
  /** 0–1: how much of the horde shows; 0 under the main menu (the yard at rest), rising as the battle begins. */
  presence = 1;
  /**
   * Drawn size over the simulation's (collision stays the sim's): a phone held upright frames the long road
   * across its narrow width, so its warriors stand a little taller to stay readable.
   */
  drawScale = 1;

  constructor(scene: THREE.Scene, private readonly M: Materials, detail = 1) {
    this.detail = detail;
    const defs: [number, BossClass | 'runner' | null, number][] = this.defs = [[11, null, 96], [23, null, 96], [37, null, 96], [71, 'brute', 6], [83, 'herald', 6], [97, 'runner', 12], [109, 'pitboss', 4]];
    const patched = new Map<THREE.Material, THREE.Material>();
    for (const [seed, boss, cap] of defs) {
      const t = buildWarrior(M, seed, boss, detail);
      const matrix = new THREE.InstancedBufferAttribute(new Float32Array(cap * 16), 16).setUsage(THREE.DynamicDrawUsage);
      const anim = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4).setUsage(THREE.DynamicDrawUsage);
      const fx = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4).setUsage(THREE.DynamicDrawUsage);
      const crowd = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4).setUsage(THREE.DynamicDrawUsage);
      const persona = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4).setUsage(THREE.DynamicDrawUsage);
      const glow = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4).setUsage(THREE.DynamicDrawUsage);
      const meshes = t.parts.map(({ mat, geo }) => {
        geo.setAttribute('iAnim', anim); geo.setAttribute('iFx', fx); geo.setAttribute('iCrowd', crowd); geo.setAttribute('iPersona', persona); geo.setAttribute('iGlow', glow);
        let pm = patched.get(mat);
        if (!pm) patched.set(mat, (pm = patch(mat)));
        const im = new THREE.InstancedMesh(geo, pm, cap);
        im.instanceMatrix = matrix;
        im.count = 0;
        im.frustumCulled = false;
        // enemies walk the road, which the key light's shadows never reach (measured: 0 px change): skip the 5 PCF taps
        im.castShadow = false; im.receiveShadow = false;
        scene.add(im);
        return im;
      });
      this.tpls.push({ t, meshes, matrix, anim, fx, crowd, persona, glow, slots: new Array(cap).fill(null), cap, used: 0 });
    }
    const total = this.tpls.reduce((a, t) => a + t.cap, 0);
    this.blobs = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ map: (M.blob as THREE.MeshBasicMaterial).map, transparent: true, depthWrite: false, opacity: 0.75 }), total);
    this.blobs.count = 0; this.blobs.frustumCulled = false; this.blobs.renderOrder = 1;
    this.blobs.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    scene.add(this.blobs);
  }

  /**
   * Rebuild every enemy model at another segment detail, keeping all live enemies and their instance data
   * (the per-instance attributes move to the new geometries). Used for profiling and adaptive quality.
   */
  setDetail(detail: number) {
    if (Math.abs(detail - this.detail) < 1e-3) return;
    this.detail = detail;
    this.defs.forEach(([seed, boss], ti) => {
      const T = this.tpls[ti], t = buildWarrior(this.M, seed, boss, detail);
      t.parts.forEach(({ geo }, k) => {
        const mesh = T.meshes[k], old = mesh.geometry;
        for (const name of ['iAnim', 'iFx', 'iCrowd', 'iPersona', 'iGlow']) geo.setAttribute(name, old.getAttribute(name));
        mesh.geometry = geo;
        old.dispose();
      });
      T.t = t;
    });
  }

  /** Follow a simulation: views appear for its enemies as they spawn. */
  attach(sim: Sim) { this.sim = sim; this.tick(); }

  get(id: number): Enemy | undefined { return this.byId.get(id); }

  /** After each sim tick: adopt newly spawned enemies and refresh records (a restored snapshot brings new objects). */
  tick() {
    const sim = this.sim; if (!sim) return;
    for (const e of sim.state.enemies) {
      const v = this.byId.get(e.id);
      if (v) { v.e = e; continue; }
      this.adopt(e);
    }
    // a view whose enemy vanished without a death or breach (e.g. after a restore) simply goes
    for (const v of this.enemies) if (!v.dying && !v.leaked && !sim.enemy(v.id)) v.dying = 1;
  }

  private adopt(e: SimEnemy): Enemy | null {
    const T = this.tpls[e.tpl];
    const slot = T.slots.indexOf(null);
    if (slot < 0) {
      // the sim enemy still fights; only its body is missing — the template caps must cover the sim's
      if (import.meta.env?.DEV && !this.warnedFull) { this.warnedFull = true; console.warn(`[horde] no render slot for template ${e.tpl}: raise its cap`); }
      return null;
    }
    const v: Enemy = {
      id: e.id, e, cls: e.cls, tpl: e.tpl, slot, dying: 0, triumph: 0, leaked: false,
      phase: Math.random() * 6.28, gait: 1, renderedS: e.s, renderedSide: e.side,
      hit: 0, hitKind: 'r', glanceDir: 0, glanceT: 0, reveal: e.reveal, ry: Math.PI / 2, pos: new THREE.Vector3(),
    };
    T.slots[slot] = v;
    T.used = Math.max(T.used, slot + 1);
    this.enemies.push(v); this.byId.set(v.id, v);
    this.place(v, 1);
    return v;
  }

  events(events: readonly SimEvent[]) {
    for (const ev of events) {
      if (ev.type === 'hit') { const v = this.byId.get(ev.id); if (v) { v.hit = 1; v.hitKind = ev.kind; } }
      else if (ev.type === 'death') { const v = this.byId.get(ev.id); if (v) { v.hitKind = ev.kind; this.kill(v); } }
      else if (ev.type === 'leak') { const v = this.byId.get(ev.id); if (v) { v.leaked = true; v.triumph = 1e-4; } }
    }
  }

  private kill(v: Enemy) {
    v.dying = 0.001;
    // the living nearby turn their heads toward the fallen
    for (const o of this.enemies) {
      if (!o.e.alive || o.dying || o.glanceT > 0.6) continue;
      const dx = v.pos.x - o.pos.x, dz = v.pos.z - o.pos.z, d = Math.hypot(dx, dz);
      if (d > 1.4 || d < 1e-3) continue;
      const right = -(dx * Math.cos(o.ry) - dz * Math.sin(o.ry)) / d;
      o.glanceDir = Math.max(-1, Math.min(1, right * 1.6)); o.glanceT = 1.2;
    }
  }

  private place(v: Enemy, alpha: number, dt = 0) {
    const e = v.e, routes = this.sim!.routes;
    // once the sim lets go of an enemy it stands where it fell
    const a = e.alive ? alpha : 1;
    const s = e.prevS + (e.s - e.prevS) * a;
    const side = e.prevSide + (e.side - e.prevSide) * a;
    const dir = routeAt(routes, e.lane, s, v.pos, _direction, e.laneZ);
    v.pos.x -= dir.z * side; v.pos.z += dir.x * side;
    const target = Math.atan2(dir.x, dir.z);
    let d = target - v.ry; d = Math.atan2(Math.sin(d), Math.cos(d));
    v.ry += d * (1 - Math.exp(-dt * 11));
    v.reveal = emergenceAt(v.pos.x) * this.presence;
    if (e.alive && e.timeStop <= 0 && e.frozen <= 0) {
      const advanced = s - v.renderedS, travelled = Math.hypot(advanced, side - v.renderedSide);
      const stride = (e.cls === 'runner' ? 0.84 : e.cls === 'pitboss' ? 0.64 : isBoss(e.cls) ? 0.56 : 0.72) * e.scale * e.stride;
      const beat = Math.floor(v.phase / Math.PI);
      v.phase += travelled * (advanced < -1e-7 ? -1 : 1) * (Math.PI * 2 / stride);
      if (dt > 0 && e.cls !== 'soldier' && v.reveal > 0.3 && Math.floor(v.phase / Math.PI) !== beat) this.footfalls.push(v);
      if (dt > 0) v.gait += ((travelled > 1e-7 ? 1 : 0) - v.gait) * (1 - Math.exp(-dt * 10));
    }
    v.renderedS = s;
    v.renderedSide = side;
  }

  /** Render-rate update: interpolate, animate, upload instance buffers. */
  render(alpha: number, dt: number) {
    this.footfalls.length = 0;
    for (const T of this.tpls) {
      const mat = T.matrix.array as Float32Array, an = T.anim.array as Float32Array, fx = T.fx.array as Float32Array, crowd = T.crowd.array as Float32Array, persona = T.persona.array as Float32Array, glow = T.glow.array as Float32Array;
      for (let i = 0; i < T.used; i++) {
        const v = T.slots[i];
        if (!v) { _m.makeScale(0, 0, 0).toArray(mat, i * 16); continue; }
        const e = v.e;
        this.place(v, alpha, dt);
        v.hit = Math.max(0, v.hit - dt * 5);
        if (v.glanceT > 0 && e.timeStop <= 0) v.glanceT = Math.max(0, v.glanceT - dt);
        if (v.dying > 0) v.dying = Math.min(1, v.dying + dt * 1.35);
        let attacking = e.attacking, meleePhase = e.meleePhase;
        if (v.leaked) {
          // through the gate: halt under the arch, weapon raised in triumph, then fade into the fire
          v.triumph = Math.min(1, v.triumph + dt / 0.8);
          attacking = 1; meleePhase = Math.min(0.42, v.triumph * 0.9);
          v.reveal *= 1 - THREE.MathUtils.smoothstep(v.triumph, 0.55, 1);
        }
        const alive = e.alive && !v.dying && !v.leaked;
        const sink = v.dying * 0.15;
        const sc = e.scale * this.drawScale * (1 + v.hit * 0.08);
        _q.setFromAxisAngle(_up, v.ry);
        let lift = 0;
        if (e.hop > 0) {
          // parabolic arc, body pitched forward into the jump and back as it lands
          lift = 4 * e.hop * (1 - e.hop) * e.hopHeight;
          _q.multiply(_q2.setFromAxisAngle(_x, Math.sin(e.hop * Math.PI * 2) * 0.28 + Math.sin(e.hop * Math.PI) * 0.15));
        }
        _m.compose(_p.set(v.pos.x, lift - sink, v.pos.z), _q, _s.set(sc * (0.9 + e.persona * 0.2) * (1 + v.hit * 0.05), sc * (1.05 - e.persona * 0.1) * (1 - v.hit * 0.06), sc)).toArray(mat, i * 16);
        an[i * 4] = v.phase; an[i * 4 + 1] = v.hit; an[i * 4 + 2] = v.dying; an[i * 4 + 3] = e.slow;
        fx[i * 4] = e.poison;
        fx[i * 4 + 1] = v.reveal;
        fx[i * 4 + 2] = e.cls === 'runner' ? 1 : 0;
        const drive = v.gait * (isBoss(e.cls) ? 0.65 : 1);
        fx[i * 4 + 3] = !alive ? 0 : e.timeStop > 0 ? -4 - drive : e.frozen > 0 ? -1 - drive : (e.hop > 0 ? 0.25 : 1) * drive * (1 - attacking);
        // boss seams: strength, violet (pit lord) or ember, and how worn down it is
        const boss = isBoss(e.cls);
        glow[i * 4] = boss ? 1 : 0; glow[i * 4 + 1] = e.cls === 'pitboss' ? 1 : 0; glow[i * 4 + 2] = boss ? 1 - Math.max(0, e.hp / e.maxHp) : 0;
        // glance envelope: snap toward the fallen, hold, ease back
        glow[i * 4 + 3] = alive && v.glanceT > 0 ? v.glanceDir * Math.min(1, (1.2 - v.glanceT) * 6) * Math.min(1, v.glanceT * 2) : 0;
        persona[i * 4] = e.persona; persona[i * 4 + 1] = e.swing; persona[i * 4 + 2] = meleePhase; persona[i * 4 + 3] = attacking;
        crowd[i * 4] = e.bumpSide; crowd[i * 4 + 1] = e.bumpForward; crowd[i * 4 + 2] = e.cls === 'pitboss' ? 1 : 0;
        const k = v.hitKind;
        crowd[i * 4 + 3] = k === 'r' ? 0 : k === 'b' ? 1 : k === 'y' ? 2 : k === 'a' ? 4 : k === 'v' ? 6 : k === 't' ? 7 : k === 'o' ? 8 : k === 'k' ? 0 : k === 's' ? 1 : 3;
      }
    }
    // remove finished
    for (let k = this.enemies.length - 1; k >= 0; k--) {
      const v = this.enemies[k];
      if (v.dying >= 1 || (v.leaked && v.triumph >= 1)) {
        const T = this.tpls[v.tpl];
        T.slots[v.slot] = null;
        while (T.used > 0 && !T.slots[T.used - 1]) T.used--;
        this.enemies.splice(k, 1); this.byId.delete(v.id);
      }
    }
    for (const T of this.tpls) {
      for (const mesh of T.meshes) { mesh.count = T.used; mesh.visible = T.used > 0; }
      if (!T.used) continue;
      for (const attr of [T.matrix, T.anim, T.fx, T.crowd, T.persona, T.glow]) {
        attr.clearUpdateRanges(); attr.addUpdateRange(0, T.used * attr.itemSize); attr.needsUpdate = true;
      }
    }
    // blob shadows
    let n = 0;
    for (const v of this.enemies) {
      if (v.dying >= 1) continue;
      const e = v.e, k = (1 - v.dying) * v.reveal * (e.hop > 0 ? 1 - 0.35 * Math.sin(e.hop * Math.PI) : 1);
      this.blobs.setMatrixAt(n++, _m.compose(_p.set(v.pos.x + 0.03, 0.02, v.pos.z), _q.identity(), _s.set(0.95 * e.scale * k * 1.4, 1, 0.7 * e.scale * k * 1.4)));
    }
    this.blobs.count = n;
    if (n) { this.blobs.instanceMatrix.clearUpdateRanges(); this.blobs.instanceMatrix.addUpdateRange(0, n * 16); this.blobs.instanceMatrix.needsUpdate = true; }
  }

  /** World-space point to aim at (chest height). */
  aim(v: Enemy, out: THREE.Vector3) { return out.set(v.pos.x, 0.85 * v.e.scale * this.drawScale, v.pos.z); }

  template(v: Enemy) { return this.tpls[v.tpl].t; }
}
